import { requestRecordingPermission } from "@memorang/helpers/device";
import { useTranslation } from "react-i18next";
import SessionConfig from "../screens/SessionConfig";

import { diagnosticItems } from "@constants/mocked-data";
import { events } from "@constants/tracking";
import { useSession } from "@features/session/hooks/useSession";
/* -----------------Helpers & Hooks--------------- */
import { getParentMetadata } from "@helpers/content/metadata";
import { groupTagsByType, hasProductAccess } from "@helpers/content/tag";
import { isWeb } from "@helpers/platform";
import useAnalytics from "@hooks/useAnalytics";
import useMixedItemDist from "@hooks/useMixedItemDist";
import { useProductMetadata } from "@hooks/useProduct";
import type { Distribution } from "@memorang/types";
import { ConfirmDialog } from "@memorang/ui/components/ConfirmDialog";
import Loader from "@memorang/ui/components/Loader";
import { MicrophonePermissionDialog } from "@memorang/ui/components/MicroPhonePermissionDialog";
import { router, useLocalSearchParams } from "expo-router";
import { useMemo, useState } from "react";

import { isI18nKeyExists } from "@helpers/common";
import { useAppStore } from "@hooks/useAppStore";
import { titleCase } from "@memorang/helpers/common";
import Box from "@memorang/ui/components/Box";
import { defaultDistributionFilterItems } from "../constants/options";
import {
	calculateProficiencyBasedDistribution,
	tagIds,
} from "../helpers/filter";

const SessionConfigContainer = ({
	propSelectedTagIds,
	toggleShowPurchaseDialog,
	handleClose,
	propStudyRecommenedTopics,
}: {
	propSelectedTagIds?: number[];
	toggleShowPurchaseDialog?: () => void;
	handleClose?: () => void;
	propStudyRecommenedTopics?: boolean;
}) => {
	const { t } = useTranslation();
	const { selectedTagIds, studyRecommenedTopics } = useLocalSearchParams<{
		selectedTagIds: string;
		studymore: string;
		studyRecommenedTopics?: string;
	}>();

	const {
		showErrorDialog,
		toggleShowErrorDialog,
		isCreatingSession,
		handleCreateCommonSession,
	} = useSession();

	const finalSelectedTagIds =
		propSelectedTagIds ||
		(selectedTagIds
			? selectedTagIds.split(",").map((id) => Number(id.trim()))
			: []);
	const finalStudyRecommenedTopics =
		propStudyRecommenedTopics || studyRecommenedTopics === "true";

	const showPointsBasedScore = useAppStore(
		(state) => state.reportView?.formative?.showPointsBasedScore,
	);

	const crossTagSelection = useAppStore(
		(state) => state.app.sessionConfig?.crossTagSelection,
	);

	const { data: mixedItemDistResponse, isRefetching } = useMixedItemDist(
		true,
		"GET_STUDYPACK_DISTRIBUTION",
	);

	const sessionConfigFilters = useAppStore(
		(state) =>
			state.app.sessionConfig?.filters || defaultDistributionFilterItems,
	);
	const tagType = useAppStore((state) => state.app.tagType);

	const memoizedAnalyticsParams = useMemo(() => {
		return {
			eventName: events.sessionConfigOpened,
		};
	}, []);
	useAnalytics(memoizedAnalyticsParams, !isRefetching);
	const { data: productMetadata, isLoading: isProductMetadataLoading } =
		useProductMetadata("STUDY_PACK");

	const [showPermissionDialog, setShowPermissionDialog] = useState(false);

	const mappedMixedItemDistResponse = mixedItemDistResponse
		? {
				...mixedItemDistResponse,
				tags: showPointsBasedScore
					? calculateProficiencyBasedDistribution(
							mixedItemDistResponse.tags,
							sessionConfigFilters,
						).mappedTags
					: mixedItemDistResponse.tags,
			}
		: undefined;
	const data = mappedMixedItemDistResponse;

	if (isRefetching || !data || isProductMetadataLoading) {
		return (
			<Box flex={1}>
				<Box
					flex={1}
					justifyContent="center"
					alignItems="center"
					height={"100%"}
				>
					<Loader />
				</Box>
			</Box>
		);
	}

	const groupSelectedTagsByType = (selectedTagIds: number[]) => {
		const groupedByType: Record<string, number[]> = {};

		for (const tagId of selectedTagIds) {
			for (const [type, typeTags] of Object.entries(tagsByType)) {
				const foundTag = typeTags.find((tag) => tag.id === tagId);
				if (foundTag) {
					if (!groupedByType[type]) {
						groupedByType[type] = [];
					}
					groupedByType[type].push(Number(tagId));
					break;
				}
			}
		}

		return Object.values(groupedByType);
	};

	//TODO:Abstract this into a function
	const { contents, tags, freeTagIds } = data;

	const { distribution } = getParentMetadata(contents);

	const hasAccess = hasProductAccess(productMetadata);
	// const hasAccess = true;
	const tagsByType = groupTagsByType(tags, "Filter", freeTagIds, hasAccess);

	const sectionTags = Object.entries(tagsByType)
		.map(([title, tags]) => ({
			title: isI18nKeyExists(`sessionConfig.${title}`)
				? t(`sessionConfig.${title}`)
				: tagType
					? titleCase(title)
					: title,
			tags: tags.sort((a, b) => a.title.localeCompare(b.title)),
		}))
		.sort((a, b) => a.title.localeCompare(b.title));

	const handleCreateSession = async (dist: Distribution, tags: number[]) => {
		if (tags.includes(tagIds.speaking)) {
			const granted = await requestRecordingPermission();
			if (!granted) {
				setShowPermissionDialog(true);
				return;
			}
		}

		const finalTags = crossTagSelection
			? groupSelectedTagsByType(tags)
			: [tags];

		handleCreateCommonSession({
			dist,
			tags: finalTags,
			handleClose,
			itemIds: [],
		});
	};

	const handleShowPurchase = () => {
		if (isWeb) {
			toggleShowPurchaseDialog?.();
		} else {
			router.push({
				pathname: "/purchase",
				params: {
					productType: "STUDY_PACK",
				},
			});
		}
	};
	const numQuestions =
		contents?.find((item) => item.type === "QUESTION_SET")?.totalCount || 0;

	const writingSectionTags = sectionTags.filter(
		(s) => s.title.toLowerCase() === "writing",
	);
	const numEssays = writingSectionTags.reduce(
		(acc, section) =>
			acc +
			section.tags.reduce((tagAcc, tag) => tagAcc + (tag.numItems || 0), 0),
		0,
	);

	const finalDistribution =
		showPointsBasedScore && mixedItemDistResponse?.tags
			? calculateProficiencyBasedDistribution(
					mixedItemDistResponse?.tags,
					sessionConfigFilters,
				).accumulatedDistribution
			: distribution;

	return (
		<>
			<SessionConfig
				available={finalDistribution}
				creatingSession={isCreatingSession}
				distributionFilterItems={sessionConfigFilters}
				handleCreateSession={handleCreateSession}
				handleShowPurchase={handleShowPurchase}
				handleClose={handleClose}
				hasAccess={hasAccess}
				diagnosticItems={diagnosticItems}
				exams={mixedItemDistResponse?.exams}
				additionalParams={{
					tagsByType,
					freeTagIds,
					hasAccess,
					studyRecommenedTopics: finalStudyRecommenedTopics,
					sectionTags,
					selectedTagIds: finalSelectedTagIds,
					numItemsMap: {
						numQuestions,
						numEssays,
					},
				}}
			/>

			<MicrophonePermissionDialog
				showDialog={showPermissionDialog}
				setShowDialog={() => {
					setShowPermissionDialog(false);
				}}
			/>

			{showErrorDialog && (
				<ConfirmDialog
					visible
					title={t("errors.sessionCreation.title")}
					body={t("errors.sessionCreation.body")}
					dismissCta="Ok"
					dismissCallback={toggleShowErrorDialog}
				/>
			)}
		</>
	);
};

export default SessionConfigContainer;
